import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';

const TRACKING_STORAGE_KEY = "tracking_params";

const ZAPIER_WEBHOOK_PLACEHOLDER = "PEGA_AQUI_TU_WEBHOOK_DE_ZAPIER";
const ZAPIER_WEBHOOK_URL =
  process.env.REACT_APP_ZAPIER_WEBHOOK_URL || ZAPIER_WEBHOOK_PLACEHOLDER;

const TRACKING_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "gclid",
  "fbclid",
  "ad_id",
  "li_fat_id"
];

const HONEYPOT_FIELD_NAME = "company_website";
const MIN_SUBMIT_TIME_MS = 1200;
const pageLoadedAt = Date.now();

function getStoredTrackingParams() {
  try {
    const stored = localStorage.getItem(TRACKING_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error("Error reading tracking params:", error);
    return {};
  }
}

function captureTrackingParamsFromUrl() {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const existingParams = getStoredTrackingParams();
    const updatedParams = { ...existingParams };

    let hasNewParams = false;

    TRACKING_PARAMS.forEach((key) => {
      const value = urlParams.get(key);

      if (value && value.trim() !== "") {
        updatedParams[key] = value.trim();
        hasNewParams = true;
      }
    });

    if (hasNewParams) {
      updatedParams.landing_page = window.location.href;
      updatedParams.referrer = document.referrer || existingParams.referrer || "";
      updatedParams.captured_at = new Date().toISOString();

      localStorage.setItem(
        TRACKING_STORAGE_KEY,
        JSON.stringify(updatedParams)
      );
    }

    return updatedParams;
  } catch (error) {
    console.error("Error capturing tracking params:", error);
    return {};
  }
}

function sanitizeKey(key) {
  return String(key || "")
    .replace(/[^a-zA-Z0-9_]/g, "")
    .slice(0, 80);
}

function sanitizeValue(value, maxLength = 2000) {
  return String(value ?? "")
    .replace(/[\u0000-\u001F\u007F]/g, "")
    .trim()
    .slice(0, maxLength);
}

function getCleanFormData(form) {
  const formData = new FormData(form);
  const cleanData = {};

  for (const [key, value] of formData.entries()) {
    const cleanKey = sanitizeKey(key);

    if (!cleanKey) continue;
    if (cleanKey === HONEYPOT_FIELD_NAME) continue;
    if (value instanceof File) continue;

    cleanData[cleanKey] = sanitizeValue(value);
  }

  return cleanData;
}

function getCleanTrackingParams() {
  const trackingParams = getStoredTrackingParams();
  const cleanTracking = {};

  [
    ...TRACKING_PARAMS,
    "landing_page",
    "referrer",
    "captured_at"
  ].forEach((key) => {
    if (trackingParams[key]) {
      cleanTracking[key] = sanitizeValue(trackingParams[key]);
    }
  });

  cleanTracking.current_page = sanitizeValue(window.location.href);
  cleanTracking.submitted_at = new Date().toISOString();

  return cleanTracking;
}

function fillTrackingInputs() {
  const trackingParams = getStoredTrackingParams();

  document.querySelectorAll("[data-tracking-param]").forEach((input) => {
    const paramName = input.getAttribute("data-tracking-param");

    if (trackingParams[paramName]) {
      input.value = trackingParams[paramName];
    }
  });

  const allTrackingInput = document.querySelector("[data-tracking-all]");

  if (allTrackingInput) {
    allTrackingInput.value = JSON.stringify(trackingParams);
  }
}

async function sendToZapier(data) {
  const body = new URLSearchParams();

  Object.entries(data).forEach(([key, value]) => {
    body.append(key, value);
  });

  await fetch(ZAPIER_WEBHOOK_URL, {
    method: "POST",
    mode: "no-cors",
    body
  });
}

function setupWebhookForms() {
  document.querySelectorAll("form[data-webhook-form]").forEach((form) => {
    if (form.dataset.webhookReady === "true") return;

    form.dataset.webhookReady = "true";

    form.addEventListener("submit", async function (event) {
      event.preventDefault();

      fillTrackingInputs();

      const honeypotInput = form.querySelector(
        `[name="${HONEYPOT_FIELD_NAME}"]`
      );

      if (honeypotInput && honeypotInput.value.trim() !== "") {
        return;
      }

      if (Date.now() - pageLoadedAt < MIN_SUBMIT_TIME_MS) {
        return;
      }

      if (
        !ZAPIER_WEBHOOK_URL ||
        ZAPIER_WEBHOOK_URL === ZAPIER_WEBHOOK_PLACEHOLDER
      ) {
        console.error("Falta configurar el webhook de Zapier.");
        alert("No se pudo enviar el formulario. Falta configurar el webhook.");
        return;
      }

      const submitButton = form.querySelector('[type="submit"]');
      const originalButtonText = submitButton?.textContent;

      try {
        if (submitButton) {
          submitButton.disabled = true;
          submitButton.textContent = "Enviando...";
        }

        const formData = getCleanFormData(form);
        const trackingData = getCleanTrackingParams();

        const dataToSend = {
          ...formData,
          ...trackingData
        };

        await sendToZapier(dataToSend);

        form.reset();

        alert("Formulario enviado correctamente.");
      } catch (error) {
        console.error("Error sending form to Zapier:", error);
        alert("Ocurrió un error al enviar el formulario.");
      } finally {
        if (submitButton) {
          submitButton.disabled = false;
          submitButton.textContent = originalButtonText || "Enviar";
        }
      }
    });
  });
}

// Captura UTMs desde la URL
captureTrackingParamsFromUrl();

// Deja disponible la función global para usarla en formularios o fetch
window.getTrackingParams = getStoredTrackingParams;

// Render normal de React
const root = ReactDOM.createRoot(document.getElementById('root'));

root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

// Configura formularios después de que React renderiza
setTimeout(() => {
  fillTrackingInputs();
  setupWebhookForms();
}, 0);

// Detecta formularios que React pueda renderizar después
const observer = new MutationObserver(() => {
  fillTrackingInputs();
  setupWebhookForms();
});

observer.observe(document.body, {
  childList: true,
  subtree: true
});

reportWebVitals();
